import axios from 'axios'
import type { AxiosProgressEvent } from 'axios'

import {
  uploadedFileSchema,
  type UploadedFile,
  type FileUploadOptions,
} from '@/types/api/file'
import {
  publicFormSchemaResponseSchema,
  type PublicFormSchema,
} from '@/types/api/form'

import { appendEncodingFields } from './file.service'

// Public API client — no auth headers, separate axios instance
const publicApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083',
  headers: { 'Content-Type': 'application/json' },
})

export async function getPublicFormSchema(token: string): Promise<PublicFormSchema> {
  const response = await publicApiClient.get(`/forms/public/${token}`)
  return publicFormSchemaResponseSchema.parse(response.data)
}

export interface PublicSubmitRequest {
  submitterName: string
  submitterEmail: string
  values: Array<{ formFieldId: string; value: string }>
}

export async function submitPublicForm(
  token: string,
  data: PublicSubmitRequest
): Promise<void> {
  await publicApiClient.post(`/forms/public/${token}/submit`, data)
}

// ── File uploads (token-scoped, rate limited per token + IP) ─────────────────

/** Uploads a document for a FILE_UPLOAD field of a public form link. */
export async function uploadPublicFormFile(
  token: string,
  formFieldId: string,
  file: File,
  options?: FileUploadOptions
): Promise<UploadedFile> {
  const formData = new FormData()
  formData.append('formFieldId', formFieldId)
  formData.append('file', file)
  appendEncodingFields(formData, options)

  const response = await publicApiClient.post(
    `/forms/public/${token}/upload`,
    formData,
    {
      // Axios lets the browser set the multipart boundary for FormData bodies.
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: options?.signal,
      onUploadProgress: (event: AxiosProgressEvent) => {
        if (!options?.onProgress) return
        const total = event.total ?? 0
        if (total > 0) options.onProgress(Math.min(100, Math.round((event.loaded / total) * 100)))
      },
    }
  )
  return uploadedFileSchema.parse(response.data)
}

/** Fresh pre-signed URL for a file uploaded through this link (preview/download). */
export async function getPublicFileUrl(token: string, key: string): Promise<string> {
  const response = await publicApiClient.get(`/forms/public/${token}/file-url`, {
    params: { key },
  })
  return response.data as string
}
